import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowUp,
  BookOpen,
  ChevronDown,
  FileText,
  GitBranch,
  Image as ImageIcon,
  ListChecks,
  Paperclip,
  PenLine,
  RefreshCw,
  Wrench,
} from 'lucide-react';
import { resolveInputDirection } from '../../utils/textUtils';
import { filterDocumentAttachments } from '../../utils/fileAttachments';
import { directionForLanguage } from '../../i18n/config';
import './ChatComposer.css';

const TEXTAREA_MIN_PX = 40;
const TEXTAREA_MAX_PX = 160;

export type AgentComposerMode = 'suggestions' | 'draft' | 'guided_workflow' | 'skills';

export type ChatComposerSubmitPayload = {
  text: string;
  attachments: File[];
};

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (payload: ChatComposerSubmitPayload) => void | Promise<void>;
  disabled?: boolean;
  submitting?: boolean;
  placeholder?: string;
  layout: 'initial' | 'sticky';
  label?: React.ReactNode;
  submitTitle?: string;
  /** Slightly gray card surface (e.g. after first message in thread) */
  followUp?: boolean;
  /** Agent behavior: review issues vs. draft into the document */
  agentMode?: AgentComposerMode;
  onAgentModeChange?: (mode: AgentComposerMode) => void;
  /** When set, show a book icon that calls this callback when clicked. */
  onPlaybookIconClick?: () => void;
  /** Selected playbook chip label — when set a chip is shown with an X to clear. */
  selectedPlaybookLabel?: string;
  onClearPlaybook?: () => void;
  /** Disable only the playbook icon (e.g. no project linked). */
  playbookDisabled?: boolean;
  /** Show Suggestions / Draft / Workflows / Skills picker. Agent only — hide in Editor and Ask. */
  showAgentModeSelector?: boolean;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  submitting = false,
  placeholder = '',
  layout,
  label,
  submitTitle,
  followUp = false,
  agentMode = 'suggestions',
  onAgentModeChange,
  onPlaybookIconClick,
  selectedPlaybookLabel,
  onClearPlaybook,
  playbookDisabled = false,
  showAgentModeSelector = true,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const resolvedSubmitTitle = submitTitle ?? t('composer.send');
  const kbdMarks = {
    enter: <kbd className="ag-kbd" dir="ltr" />,
    shift: <kbd className="ag-kbd" dir="ltr" />,
    send: <bdi className="ag-composer-hint-part" />,
    newline: <bdi className="ag-composer-hint-part" />,
  };
  const modeLabel =
    agentMode === 'suggestions'
      ? t('composer.modes.suggestions')
      : agentMode === 'draft'
        ? t('composer.modes.draft')
        : agentMode === 'guided_workflow'
          ? t('composer.modes.workflows')
          : t('composer.modes.skills');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const modeDropdownRef = React.useRef<HTMLDivElement>(null);
  const [imageFiles, setImageFiles] = React.useState<File[]>([]);
  const [attachmentFiles, setAttachmentFiles] = React.useState<File[]>([]);
  const [dragActive, setDragActive] = React.useState(false);
  const [agentModeMenuOpen, setAgentModeMenuOpen] = React.useState(false);

  const adjustTextareaHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const full = el.scrollHeight;
    const next = Math.min(Math.max(full, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = full > TEXTAREA_MAX_PX ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  React.useEffect(() => {
    if (!agentModeMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (modeDropdownRef.current?.contains(e.target as Node)) return;
      setAgentModeMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAgentModeMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [agentModeMenuOpen]);

  function trySubmit() {
    if (disabled || submitting) return;
    const t = value.trim();
    if (!t && attachmentFiles.length === 0 && !selectedPlaybookLabel) return;
    const attachments = [...attachmentFiles];
    setImageFiles([]);
    setAttachmentFiles([]);
    void onSubmit({ text: t, attachments });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      trySubmit();
    }
  }

  function onPickImages(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list?.length) return;
    setImageFiles((prev) => [...prev, ...Array.from(list)]);
    e.target.value = '';
  }

  function removeImageAt(index: number) {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function onPickAttachments(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list?.length) return;
    const next = filterDocumentAttachments(list);
    if (next.length) setAttachmentFiles((prev) => [...prev, ...next]);
    e.target.value = '';
  }

  function removeAttachmentAt(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    e.dataTransfer.dropEffect = 'copy';
  }

  function onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setDragActive(true);
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragActive(false);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (busy) return;
    const list = e.dataTransfer.files;
    if (!list?.length) return;
    const next = filterDocumentAttachments(list);
    if (next.length) setAttachmentFiles((prev) => [...prev, ...next]);
  }

  const busy = disabled || submitting;

  /** Typed content wins; when empty, infer from placeholder (Hebrew workflows, etc.). */
  const inputDir = resolveInputDirection(value, placeholder);

  return (
    <div
      className={`ag-composer ${layout === 'sticky' ? 'ag-composer--sticky' : 'ag-composer--initial'}`}
      dir={chromeDir}
    >
      {label}
      <div
        className={`ag-composer-card${followUp ? ' ag-composer-card--followup' : ''}${dragActive ? ' ag-composer-card--drag' : ''}`}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {(imageFiles.length > 0 || attachmentFiles.length > 0 || selectedPlaybookLabel) && (
          <div className="ag-image-chips">
            {selectedPlaybookLabel && (
              <button
                type="button"
                className="ag-image-chip ag-playbook-chip"
                onClick={onClearPlaybook}
                title={t('composer.removePlaybook')}
                aria-label={t('composer.removePlaybook')}
              >
                <BookOpen size={12} aria-hidden />
                <span className="ag-image-chip-name">{selectedPlaybookLabel}</span>
              </button>
            )}
            {imageFiles.map((f, i) => (
              <button
                key={`img-${f.name}-${i}`}
                type="button"
                className="ag-image-chip"
                onClick={() => removeImageAt(i)}
                title={t('composer.removeImage')}
              >
                <ImageIcon size={12} aria-hidden />
                <span className="ag-image-chip-name">{f.name}</span>
              </button>
            ))}
            {attachmentFiles.map((f, i) => (
              <button
                key={`att-${f.name}-${i}`}
                type="button"
                className="ag-image-chip ag-attachment-chip"
                onClick={() => removeAttachmentAt(i)}
                title={t('composer.removeAttachment')}
              >
                <FileText size={12} aria-hidden />
                <span className="ag-image-chip-name">{f.name}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="ag-composer-textarea"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={busy}
          dir={inputDir}
          title={
            followUp
              ? resolvedSubmitTitle
              : t('composer.enterToSendTitle')
          }
        />
        <div className="ag-composer-toolbar">
          {!followUp && (
            <div className="ag-composer-toolbar-hint-row">
              <span className="ag-composer-toolbar-hint">
                <Trans t={t} i18nKey="composer.enterToSend" components={kbdMarks} />
              </span>
            </div>
          )}
          <div className="ag-composer-toolbar-actions">
            {showAgentModeSelector && (
            <div className="ag-composer-toolbar-mode">
              <div
                ref={modeDropdownRef}
                className={`ag-mode-dropdown${followUp ? ' ag-mode-dropdown--placement-up' : ' ag-mode-dropdown--placement-down'}`}
                data-open={agentModeMenuOpen ? 'true' : 'false'}
                data-placement={followUp ? 'up' : 'down'}
              >
                <button
                  type="button"
                  className="ag-mode-dropdown-trigger"
                  disabled={submitting}
                  aria-haspopup="listbox"
                  aria-expanded={agentModeMenuOpen}
                  aria-label={t('composer.agentMode')}
                  title={t('composer.modes.title')}
                  onClick={() => !submitting && setAgentModeMenuOpen((o) => !o)}
                >
                  {agentMode === 'suggestions' ? (
                    <ListChecks size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  ) : agentMode === 'draft' ? (
                    <PenLine size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  ) : agentMode === 'guided_workflow' ? (
                    <GitBranch size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  ) : (
                    <Wrench size={14} strokeWidth={2} className="ag-mode-dropdown-icon" aria-hidden />
                  )}
                  <span className="ag-mode-dropdown-label" dir="ltr">
                    {modeLabel}
                  </span>
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    className={`ag-mode-dropdown-chevron${agentModeMenuOpen ? ' ag-mode-dropdown-chevron--open' : ''}`}
                    aria-hidden
                  />
                </button>
                {agentModeMenuOpen && (
                  <ul
                    className="ag-mode-dropdown-menu"
                    dir="ltr"
                    role="listbox"
                    aria-label={t('composer.chooseAgentMode')}
                  >
                    <li role="option" aria-selected={agentMode === 'suggestions'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'suggestions' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('suggestions');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <ListChecks size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title" dir="ltr">
                            {t('composer.modes.suggestions')}
                          </span>
                          <span className="ag-mode-dropdown-item-desc" dir="auto">
                            {t('composer.modes.suggestionsDesc')}
                          </span>
                        </span>
                      </button>
                    </li>
                    <li role="option" aria-selected={agentMode === 'draft'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'draft' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('draft');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <PenLine size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title" dir="ltr">
                            {t('composer.modes.draft')}
                          </span>
                          <span className="ag-mode-dropdown-item-desc" dir="auto">
                            {t('composer.modes.draftDesc')}
                          </span>
                        </span>
                      </button>
                    </li>
                    <li role="option" aria-selected={agentMode === 'guided_workflow'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'guided_workflow' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('guided_workflow');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <GitBranch size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title" dir="ltr">
                            {t('composer.modes.workflows')}
                          </span>
                          <span className="ag-mode-dropdown-item-desc" dir="auto">
                            {t('composer.modes.workflowsDesc')}
                          </span>
                        </span>
                      </button>
                    </li>
                    <li role="option" aria-selected={agentMode === 'skills'}>
                      <button
                        type="button"
                        className={`ag-mode-dropdown-item${agentMode === 'skills' ? ' ag-mode-dropdown-item--active' : ''}`}
                        onClick={() => {
                          onAgentModeChange?.('skills');
                          setAgentModeMenuOpen(false);
                        }}
                      >
                        <Wrench size={14} strokeWidth={2} aria-hidden />
                        <span>
                          <span className="ag-mode-dropdown-item-title" dir="ltr">
                            {t('composer.modes.skills')}
                          </span>
                          <span className="ag-mode-dropdown-item-desc" dir="auto">
                            {t('composer.modes.skillsDesc')}
                          </span>
                        </span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
            )}
            <div className="ag-composer-toolbar-right">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="ag-hidden-file"
              onChange={onPickImages}
              aria-hidden
              tabIndex={-1}
            />
            <input
              ref={attachmentInputRef}
              type="file"
              accept=".docx,.pdf,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              multiple
              className="ag-hidden-file"
              onChange={onPickAttachments}
              aria-hidden
              tabIndex={-1}
            />
            <button
              type="button"
              className="ag-icon-ghost"
              title={t('composer.uploadImage')}
              disabled={busy}
              onClick={() => imageInputRef.current?.click()}
            >
              <ImageIcon size={18} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              className="ag-icon-ghost"
              title={t('composer.attachDocument')}
              disabled={busy}
              onClick={() => attachmentInputRef.current?.click()}
            >
              <Paperclip size={18} strokeWidth={1.75} />
            </button>
            {onPlaybookIconClick && (
              <button
                type="button"
                className={`ag-icon-ghost${selectedPlaybookLabel ? ' ag-icon-ghost--active' : ''}`}
                title={
                  playbookDisabled
                    ? t('composer.playbookDisabled')
                    : selectedPlaybookLabel
                    ? t('composer.changePlaybook')
                    : t('composer.usePlaybook')
                }
                disabled={busy || playbookDisabled}
                aria-label={t('composer.selectPlaybook')}
                onClick={onPlaybookIconClick}
              >
                <BookOpen size={18} strokeWidth={1.75} />
              </button>
            )}
            <button
              type="button"
              className="ag-send-circle"
              onClick={trySubmit}
              disabled={busy || (!value.trim() && attachmentFiles.length === 0 && !selectedPlaybookLabel)}
              title={resolvedSubmitTitle}
            >
              {submitting ? (
                <RefreshCw size={14} className="spinning ag-send-icon" />
              ) : (
                <ArrowUp size={14} strokeWidth={2.5} className="ag-send-icon" />
              )}
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
